import { toast } from "react-toastify";
import fetchApi from "../../../utils/fetchApi";
import {
  cleanCKEditorTempImages,
  uploadCKEditorTempImageURL,
} from "../api/url";
import global from "../../../config/global";

const toWebUploadPath = (data) => {
  const normalized = String(data || "").replace(/\\/g, "/");
  const uploadsIndex = normalized.lastIndexOf("uploads/");
  const relative =
    uploadsIndex >= 0 ? normalized.slice(uploadsIndex) : normalized;
  return relative.replace(/^\//, "");
};

/**
 * Upload helper
 */
const uploadTempImage = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  const { status, message, data } = await fetchApi.post(
    uploadCKEditorTempImageURL,
    formData,
    true,
  );
  if (status === "success") return toWebUploadPath(data);
  toast.error(message);
  return undefined;
};

/**
 * Main plugin: registers both the file-loader adapter and the paste-handler.
 */
export default function MyCustomUploadAdapterPlugin(editor) {
  // 1) file repository → your existing adapter for files/blobs
  editor.plugins.get("FileRepository").createUploadAdapter = (loader) =>
    new MyUploadAdapter(loader);

  // 2) clipboard-paste handler for existing <img src="...">
  editor.editing.view.document.on(
    "clipboardInput",
    (evt, data) => handleClipboardPaste(evt, data, editor),
    { priority: "high" },
  );
}

class MyUploadAdapter {
  constructor(loader) {
    this.loader = loader;
  }

  async upload() {
    const file = await this.loader.file;
    const data = await uploadTempImage(file);
    if (!data) throw new Error("Upload failed");
    // track for later cleanup
    const stored = JSON.parse(
      localStorage.getItem("ckeditor_temp_images") || "[]",
    );
    stored.push(data);
    localStorage.setItem("ckeditor_temp_images", JSON.stringify(stored));
    return { default: global.baseUrl + data };
  }

  abort() {
    // no special abort logic
  }
}

/**
 * On save (or unmount), call this to remove any unused temp images on the server.
 */
export const cleanupTempImages = () => {
  const stored = JSON.parse(
    localStorage.getItem("ckeditor_temp_images") || "[]",
  );
  if (stored.length) {
    const blob = new Blob([JSON.stringify({ paths: stored })], {
      type: "application/json",
    });
    navigator.sendBeacon(cleanCKEditorTempImages, blob);
    localStorage.removeItem("ckeditor_temp_images");
  }
};

/**
 * Parses pasted HTML, finds any <img src="..."> whose URL matches
 * your temp-storage pattern (or any URL), re-uploads each via your API,
 * replaces in the HTML fragment, then inserts it into the editor.
 */
async function handleClipboardPaste(evt, data, editor) {
  const html = data.dataTransfer.getData("text/html");
  if (!html) return;

  // Quick check: do we have any <img> tags?
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const imgs = Array.from(doc.querySelectorAll("img"));

  if (!imgs.length) return;

  // Prevent the default paste so we can re-insert our modified HTML
  evt.stop();

  // For each img, fetch its blob, upload, and remember the old→new URL.
  const replacements = await Promise.all(
    imgs.map(async (img) => {
      try {
        const resp = await fetch(img.src);
        const blob = await resp.blob();
        const file = new File([blob], "pasted.png", {
          type: blob.type,
        });
        const dataPath = await uploadTempImage(file);
        // track for cleanup
        const stored = JSON.parse(
          localStorage.getItem("ckeditor_temp_images") || "[]",
        );
        stored.push(dataPath);
        localStorage.setItem("ckeditor_temp_images", JSON.stringify(stored));
        const newUrl = global.baseUrl + dataPath;
        return { oldUrl: img.src, newUrl };
      } catch (e) {
        console.error("Paste-image upload failed", e);
        return null;
      }
    }),
  );

  // Build the new HTML fragment
  let newHtml = html;
  replacements.forEach((rep) => {
    if (rep) {
      // replace all occurrences of oldUrl with newUrl
      newHtml = newHtml.split(rep.oldUrl).join(rep.newUrl);
    }
  });

  // Insert into the editor at the current selection
  editor.model.change((writer) => {
    const viewFragment = editor.data.processor.toView(newHtml);
    const modelFragment = editor.data.toModel(viewFragment);
    editor.model.insertContent(modelFragment, editor.model.document.selection);
  });
}
